import { DateTime } from 'luxon';
import type { ExpiringProgramSummary } from '@/types/v2/DashboardWidget';
import type { TableSort, TableSortOption } from '@/utilities/TableSort';

type SortField = keyof Pick<
  ExpiringProgramSummary,
  'expiresAt' | 'accountName' | 'programName' | 'maxLimitInclAttachment' | 'retention' | 'totalPremiumSum'
>;

const SOONEST_EXPIRATION: TableSort = { key: 'expiresAt', direction: 'ascending' };
const CLIENT_A_TO_Z: TableSort = { key: 'accountName', direction: 'ascending' };

export const PROGRAMS_EXPIRING_SORT_OPTIONS: TableSortOption[] = [
  {
    value: 'expiration',
    label: 'Expiration',
    description: 'Soonest expiration first',
    reversedDescription: 'Latest expiration first',
    sorts: [SOONEST_EXPIRATION, CLIENT_A_TO_Z],
  },
  {
    value: 'client',
    label: 'Client',
    description: 'Client A–Z',
    reversedDescription: 'Client Z–A',
    sorts: [CLIENT_A_TO_Z, SOONEST_EXPIRATION],
  },
  {
    value: 'program',
    label: 'Program',
    description: 'Program A–Z',
    reversedDescription: 'Program Z–A',
    sorts: [{ key: 'programName', direction: 'ascending' }, CLIENT_A_TO_Z],
  },
  {
    value: 'limit',
    label: 'Limit',
    description: 'Highest limit first',
    reversedDescription: 'Lowest limit first',
    sorts: [{ key: 'maxLimitInclAttachment', direction: 'descending' }, SOONEST_EXPIRATION],
  },
  {
    value: 'retention',
    label: 'Retention',
    description: 'Highest retention first',
    reversedDescription: 'Lowest retention first',
    sorts: [{ key: 'retention', direction: 'descending' }, SOONEST_EXPIRATION],
  },
  {
    value: 'premium',
    label: 'Premium',
    description: 'Highest premium first',
    reversedDescription: 'Lowest premium first',
    sorts: [{ key: 'totalPremiumSum', direction: 'descending' }, SOONEST_EXPIRATION],
  },
];

export const DEFAULT_PROGRAMS_EXPIRING_SORT = PROGRAMS_EXPIRING_SORT_OPTIONS[0].value;

function sortableValue(program: ExpiringProgramSummary, field: SortField): string | number | null {
  const value = program[field];
  if (value == null || value === '') return null;
  if (field === 'expiresAt') return DateTime.fromISO(String(value), { zone: 'utc' }).toMillis();
  return value;
}

function compareValues(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

/** Missing values sit below every real value in both directions, so reversing never floods the top with dashes. */
export function sortExpiringPrograms(programs: ExpiringProgramSummary[], sorts: TableSort[]): ExpiringProgramSummary[] {
  return programs.slice().sort((a, b) => {
    for (const { key, direction } of sorts) {
      const aValue = sortableValue(a, key as SortField);
      const bValue = sortableValue(b, key as SortField);
      if (aValue === null || bValue === null) {
        if (aValue === bValue) continue;
        return aValue === null ? 1 : -1;
      }
      const result = compareValues(aValue, bValue);
      if (result !== 0) return direction === 'descending' ? -result : result;
    }
    return 0;
  });
}

export type ProgramsExpiringLayout = 'full' | 'compact' | 'stacked';

const FULL_LAYOUT_MIN_WIDTH = 640;
const COMPACT_LAYOUT_MIN_WIDTH = 420;

export function layoutForWidth(width: number): ProgramsExpiringLayout {
  if (width >= FULL_LAYOUT_MIN_WIDTH) return 'full';
  if (width >= COMPACT_LAYOUT_MIN_WIDTH) return 'compact';
  return 'stacked';
}

export function programsExpiringTitle(days: number): string {
  return `Programs Expiring in ${days} Days`;
}

export function programsExpiringFileName(days: number, date: DateTime): string {
  return `Programs_Expiring_${days}_Days_${date.toFormat('yyyy_MM_dd')}`;
}

function csvCell(value: string | number | null | undefined): string {
  if (value == null || value === '') return '';
  return `"${String(value).replace(/"/g, '""')}"`;
}

export function programsExpiringCsv(programs: ExpiringProgramSummary[]): string {
  const header = 'Client,Program,Limit,Retention,Premium,Expiration';
  const rows = programs.map((p) =>
    [
      p.accountName,
      p.programName,
      p.maxLimitInclAttachment,
      p.retention,
      p.totalPremiumSum,
      p.expiresAt ? DateTime.fromISO(p.expiresAt, { zone: 'utc' }).toISODate() : null,
    ]
      .map(csvCell)
      .join(','),
  );
  return [header, ...rows].join('\n');
}
